import React, { useState } from 'react';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend,
} from 'recharts';
import { LineChart as LineIcon, BarChart3, PieChart as PieIcon, Plus, Trash2, Check } from 'lucide-react';

/**
 * InsightsBuilder — Gráficas configurables por el usuario.
 * El usuario elige QUÉ métricas graficar (varias cruzadas en la misma gráfica)
 * y CÓMO verlas (líneas, barras o tortas). Cada tarjeta se guarda por usuario.
 *
 * Props:
 *  - metrics: [{ id, label, color, data: [{ label, value }] }]
 *  - storageKey: clave única de persistencia (por usuario)
 *  - defaults: configuración inicial de tarjetas
 */
const PIE_PALETTE = ['#3EB489', '#C9A84C', '#A8E6CE', '#E35D6A', '#2D4A3E', '#7FB3A0', '#D4B86A'];
const CHART_TYPES = [
  { key: 'line', label: 'Líneas', icon: LineIcon },
  { key: 'bar', label: 'Barras', icon: BarChart3 },
  { key: 'pie', label: 'Tortas', icon: PieIcon },
];
const TOOLTIP_STYLE = { background: 'rgba(14,18,17,0.96)', border: '1px solid rgba(62,180,137,0.3)', borderRadius: 12, color: '#eafff5' };

const mergeByLabel = (selected) => {
  const labels = [];
  selected.forEach((m) => m.data.forEach((p) => { if (!labels.includes(p.label)) labels.push(p.label); }));
  return labels.map((label) => {
    const row = { label };
    selected.forEach((m) => { const pt = m.data.find((p) => p.label === label); row[m.id] = pt ? pt.value : 0; });
    return row;
  });
};

const ChartCanvas = ({ card, metrics }) => {
  const selected = (card.metrics || []).map((id) => metrics.find((m) => m.id === id)).filter(Boolean);
  if (selected.length === 0) {
    return <div className="h-56 grid place-items-center text-xs text-muted-foreground">Selecciona una o más métricas para graficar.</div>;
  }

  if (card.type === 'pie') {
    let pieData;
    if (selected.length === 1) {
      pieData = selected[0].data.filter((p) => p.value !== 0).map((p) => ({ name: p.label, value: Math.abs(p.value) }));
    } else {
      pieData = selected.map((m) => ({ name: m.label, value: Math.abs(m.data.reduce((a, p) => a + p.value, 0)), fill: m.color }));
    }
    if (pieData.length === 0) return <div className="h-56 grid place-items-center text-xs text-muted-foreground">Sin datos para mostrar.</div>;
    return (
      <div className="h-56 min-h-[224px] w-full">
        <ResponsiveContainer width="100%" height={224} minWidth={0}>
          <PieChart>
            <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80} paddingAngle={3}>
              {pieData.map((d, i) => <Cell key={i} fill={d.fill || PIE_PALETTE[i % PIE_PALETTE.length]} />)}
            </Pie>
            <RTooltip contentStyle={TOOLTIP_STYLE} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  }

  const data = mergeByLabel(selected);
  const Chart = card.type === 'bar' ? BarChart : LineChart;
  return (
    <div className="h-56 min-h-[224px] w-full">
      <ResponsiveContainer width="100%" height={224} minWidth={0}>
        <Chart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(150,170,160,0.12)" />
          <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#7a8f85' }} />
          <YAxis tick={{ fontSize: 10, fill: '#7a8f85' }} width={44} />
          <RTooltip contentStyle={TOOLTIP_STYLE} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          {selected.map((m) => card.type === 'bar'
            ? <Bar key={m.id} dataKey={m.id} name={m.label} fill={m.color} radius={[6, 6, 0, 0]} />
            : <Line key={m.id} dataKey={m.id} name={m.label} stroke={m.color} strokeWidth={2} dot={false} isAnimationActive />)}
        </Chart>
      </ResponsiveContainer>
    </div>
  );
};

export const InsightsBuilder = ({ metrics, storageKey, defaults }) => {
  const [cards, setCards] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(storageKey) || 'null');
      if (Array.isArray(saved) && saved.length) return saved;
    } catch (err) { console.debug('No se pudieron leer las gráficas guardadas:', err); }
    return defaults || [{ id: 'c1', title: 'Mi gráfica', type: 'line', metrics: metrics.slice(0, 2).map((m) => m.id) }];
  });

  const persist = (next) => { setCards(next); localStorage.setItem(storageKey, JSON.stringify(next)); };
  const update = (id, patch) => persist(cards.map((c) => c.id === id ? { ...c, ...patch } : c));
  const remove = (id) => persist(cards.filter((c) => c.id !== id));
  const add = () => persist([...cards, { id: `c${Date.now()}`, title: 'Nueva gráfica', type: 'line', metrics: metrics.slice(0, 1).map((m) => m.id) }]);
  const toggleMetric = (card, mid) => {
    const has = (card.metrics || []).includes(mid);
    update(card.id, { metrics: has ? card.metrics.filter((x) => x !== mid) : [...(card.metrics || []), mid] });
  };

  return (
    <div data-testid="insights-builder">
      <div className="flex items-center justify-end mb-3">
        <button data-testid="add-chart-btn" onClick={add} className="h-9 px-4 rounded-xl text-sm font-medium border flex items-center gap-2 hover:pp-glow-mint transition-shadow">
          <Plus size={15} className="pp-mint" /> Añadir gráfica
        </button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {cards.map((card) => (
          <div key={card.id} className="pp-glass p-5" data-testid={`chart-card-${card.id}`}>
            <div className="flex items-center gap-2 mb-3">
              <input value={card.title} onChange={(e) => update(card.id, { title: e.target.value })} data-testid={`chart-title-${card.id}`}
                className="flex-1 bg-transparent text-sm font-semibold outline-none border-b border-transparent focus:border-[rgba(62,180,137,0.4)] pb-0.5" />
              <div className="flex gap-1">
                {CHART_TYPES.map((t) => (
                  <button key={t.key} onClick={() => update(card.id, { type: t.key })} title={t.label} data-testid={`chart-type-${card.id}-${t.key}`}
                    className={`w-8 h-8 rounded-lg grid place-items-center border transition-colors ${card.type === t.key ? 'pp-mint border-[#3EB489] bg-[rgba(62,180,137,0.14)]' : 'text-muted-foreground border-[rgba(150,170,160,0.2)] hover:text-foreground'}`}>
                    <t.icon size={15} />
                  </button>
                ))}
              </div>
              <button onClick={() => remove(card.id)} title="Eliminar" data-testid={`chart-remove-${card.id}`} className="w-8 h-8 rounded-lg grid place-items-center text-muted-foreground hover:text-[#E35D6A] transition-colors"><Trash2 size={15} /></button>
            </div>

            {/* Selector de métricas (chips) */}
            <div className="flex flex-wrap gap-1.5 mb-4">
              {metrics.map((m) => {
                const active = (card.metrics || []).includes(m.id);
                return (
                  <button key={m.id} onClick={() => toggleMetric(card, m.id)} data-testid={`chart-metric-${card.id}-${m.id}`}
                    className={`px-2.5 h-7 rounded-lg text-[11px] font-medium border flex items-center gap-1 transition-colors ${active ? 'text-foreground' : 'text-muted-foreground border-[rgba(150,170,160,0.2)] hover:text-foreground'}`}
                    style={active ? { borderColor: m.color, background: `${m.color}22` } : {}}>
                    <span className="w-2 h-2 rounded-full" style={{ background: m.color, opacity: active ? 1 : 0.4 }} />
                    {m.label}{active && <Check size={11} />}
                  </button>
                );
              })}
            </div>

            <ChartCanvas card={card} metrics={metrics} />
          </div>
        ))}
      </div>
      {cards.length === 0 && (
        <div className="pp-glass p-8 text-center text-sm text-muted-foreground">Aún no tienes gráficas. Pulsa “Añadir gráfica” para crear la primera.</div>
      )}
    </div>
  );
};
